import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withKeys as _withKeys, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = ["id", "aria-expanded", "aria-activedescendant", "maxlength", "value", "placeholder"]
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", {
  id: "inputDescription",
  class: "sr-only"
}, "Die Ergebnisse werden während der Eingabe aktualisiert.", -1)
const _hoisted_4 = ["aria-label", "aria-disabled"]
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = {
  role: "status",
  class: "sr-only"
}

import { ref, computed, watch, onMounted, provide } from "vue";
import useSearch, { focusedTypeAheadId, focusedTypeAheadIndex } from "@/composables/use-search";
import useDepartment from "@/composables/use-department";
import { isClassicSite, language } from "@ocm/services/ocm-object";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import { useBreakpoints, useDebounceFn, useSpeechRecognition } from "@vueuse/core";
import TypeAhead from "@/components/TypeAhead.vue";
import { typeAheadContainerKey } from "@/constants/injectionKeys";
import { skipLinks } from "@ocm/services/services.skipLinks";

const TYPE_AHEAD_LISTBOX_ID = "typeAheadListbox";


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchForm',
  setup(__props) {

const SEARCH_INPUT_ID = skipLinks.search.input.slice(1);
const { departmentCapitalized } = useDepartment();
const {
	isLoading,
	userInput,
	userInputMaxlength,
	setInputElementRef,
	openSearch,
	triggerSearch,
	deleteSearch,
	onKeyDown,
	onInput,
	isSearchVisible,
	nrOfDisplayedTypeAheadItems,
} = useSearch();

const typeAheadContainerRef = ref<HTMLDivElement | null>(null);
provide(typeAheadContainerKey, typeAheadContainerRef);

const inputEl = ref<HTMLInputElement | null>(null);
const formRef = ref<HTMLFormElement | null>(null);

const isFocusOnInput = ref(false);

onMounted(() => {
	setInputElementRef(inputEl.value);
});

const isScreen1408px = useBreakpoints({ px1408: "88em" }).greaterOrEqual("px1408");

const isInputEmpty = computed(() => userInput.value === "");

const showSearchBox = computed(
	() =>
		!isScreen1408px.value ||
		(isSearchVisible.value && isFocusOnInput.value && (isLoading.value || !!nrOfDisplayedTypeAheadItems.value))
);

function handleInputFocus() {
	isFocusOnInput.value = true;
	openSearch();
}

function handleInputBlur() {
	// delay to allow click event on typeAheadItem to be processed first
	window.setTimeout(() => {
		isFocusOnInput.value = false;
		focusedTypeAheadIndex.value = -1;
	}, 200);
}

const _debounceSearch = useDebounceFn(triggerSearch, 600);
const speech = useSpeechRecognition({ lang: language, interimResults: false });
const { isListening, isSupported: isSpeechSupported } = speech;
const triggerSpeech = () => speech.toggle(!isListening.value);
watch(speech.result, (result) => {
	speech.stop();
	userInput.value = result;
	_debounceSearch("voice");
});

const placeholderI18n = new Map([
	["ar", "بحث"],
	["de", "Suche"],
	["en", "Search"],
	["fr", "Chercher"],
	["ko", "검색"],
	["ru", "Поиск"],
	["zh", "搜索"],
]);
const placeholderClassic = placeholderI18n.get(language);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    ref_key: "formRef",
    ref: formRef,
    role: "search",
    onSubmit: _cache[5] || (_cache[5] = (event) => {event.preventDefault();_unref(triggerSearch)('keyboard');})
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["tw relative mx-4 mb-7 mt-8 h-10 focus-within:text-black-primary px1408:m-0 px1408:w-80", { 'text-gray-80': isInputEmpty.value }]),
      onKeydown: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(onKeyDown) && _unref(onKeyDown)(...args)))
    }, [
      _createElementVNode("label", {
        for: _unref(SEARCH_INPUT_ID),
        class: "sr-only"
      }, _toDisplayString(_unref(isClassicSite) ? _unref(placeholderClassic) : "Artikel suchen"), 9, _hoisted_1),
      _createElementVNode("input", {
        id: _unref(SEARCH_INPUT_ID),
        ref_key: "inputEl",
        ref: inputEl,
        role: "combobox",
        "aria-controls": TYPE_AHEAD_LISTBOX_ID,
        "aria-expanded": showSearchBox.value,
        "aria-activedescendant": _unref(focusedTypeAheadId),
        "aria-describedby": "inputDescription",
        type: "text",
        class: "h-10 w-full rounded-full bg-gray-20 px-11 text-black-primary outline outline-transparent placeholder:text-gray-80 hover:bg-gray-40 focus:bg-gray-40 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-current",
        maxlength: _unref(userInputMaxlength),
        value: _unref(userInput),
        placeholder: 
					_unref(isListening) ? 'Was suchen Sie?' : _unref(isClassicSite) ? _unref(placeholderClassic) : `Suche in: ${_unref(departmentCapitalized)}`
				,
        autocomplete: "off",
        "aria-autocomplete": "list",
        onInput: _cache[0] || (_cache[0] = ($event: any) => (_unref(onInput)($event as InputEvent))),
        onKeyup: _cache[1] || (_cache[1] = _withKeys(($event: any) => (_unref(triggerSearch)('keyboard')), ["enter"])),
        onFocus: handleInputFocus,
        onBlur: handleInputBlur
      }, null, 40, _hoisted_2),
      _hoisted_3,
      _createElementVNode("button", {
        type: "submit",
        class: "absolute left-3.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-content-center",
        "aria-label": _unref(isClassicSite) ? _unref(placeholderClassic) : 'Suchen',
        "aria-disabled": isInputEmpty.value,
        onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(triggerSearch)('click')))
      }, [
        _createVNode(SvgIcon, {
          "icon-name": "magnifying-glass",
          class: "h-5 w-5"
        })
      ], 8, _hoisted_4),
      (_unref(isSpeechSupported) && isInputEmpty.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            class: _normalizeClass(["absolute right-3.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-content-center rounded-xl", _unref(isListening) ? 'srh__bg-pulse text-white' : ' supportsHover:hover:text-black-primary']),
            "aria-label": _unref(isListening) ? 'Spracheingabe beenden' : 'Spracheingabe starten',
            onClick: triggerSpeech
          }, [
            _createVNode(SvgIcon, {
              "icon-name": "mic",
              class: "h-5 w-5"
            })
          ], 10, _hoisted_5))
        : (_openBlock(), _createElementBlock("button", {
            key: 1,
            type: "button",
            class: _normalizeClass(["absolute right-3.5 top-1/2 h-6 w-6 -translate-y-1/2 place-content-center rounded-xl", isInputEmpty.value ? 'hidden' : 'grid']),
            "aria-label": "Eingabe löschen",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(deleteSearch)()))
          }, [
            _createVNode(SvgIcon, {
              "icon-name": "close-new",
              class: "h-5 w-5"
            })
          ], 2))
    ], 34),
    _createElementVNode("div", {
      id: TYPE_AHEAD_LISTBOX_ID,
      ref_key: "typeAheadContainerRef",
      ref: typeAheadContainerRef,
      role: "listbox",
      "aria-label": "Suchvorschläge",
      class: _normalizeClass(["o-scrollbar max-h-[calc(100vh-200px)] overscroll-contain", [
				_unref(isScreen1408px)
					? 'absolute right-0 top-full z-[100] max-h-[calc(100vh-200px)] w-[calc(var(--spacing-base-unit)*125)] border-t-2 border-t-gray-40 bg-white p-4 text-sm shadow-4'
					: 'px-4 pb-4',
				{ hidden: !showSearchBox.value },
			]]),
      style: {"clip-path":"inset(0 -10px -10px -10px)"}
    }, [
      (_unref(isLoading))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createTextVNode("Loading …")
          ], 64))
        : (_openBlock(), _createBlock(TypeAhead, { key: 1 }))
    ], 2),
    _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(nrOfDisplayedTypeAheadItems) === 0 ? "Keine Suchergebnisse." : ""), 1)
  ], 544))
}
}

})